import { wallClockInstant } from "@obby/lib";
import { rentalDayString } from "./rental-start-options";
import type {
  PoolAvailabilityResponse,
  PoolAvailabilityWindow,
} from "../../../lib/checkout/pool-availability";

/**
 * How many start instants one request may ask the server to price.
 *
 * Bounds the query string (an ISO instant is 24 characters plus its separator,
 * so 192 is ~4.8KB — clear of the ~8KB a proxy will typically accept) and the
 * server's per-window work. 192 covers every realistic rental grid: a
 * 15-minute fraction across a full 24 hours is 96 starts, a 30-minute one 48.
 * The listing that prompted this offers 7 on its busiest day.
 *
 * The server applies the same ceiling (`MAX_EXPLICIT_STARTS`) and truncates.
 *
 * ⚠ Exceeding it costs the WHOLE day its quantity bound, not just the tail:
 * `rentalDayPoolCap` treats a single unknown start as making the day unknown,
 * so one truncated start unbounds the stepper. The first
 * {@link MAX_RENTAL_POOL_STARTS} starts do keep their per-start caps, which is
 * why truncating still beats skipping the request.
 *
 * ⚠ And it IS reachable on ordinary partner input — an earlier version of this
 * note claimed otherwise. A slot configured 00:00→00:00 rolls to a full
 * 0–1440 window, and at a 5-minute fraction with a one-hour hire that is 277
 * starts. The fraction is a free numeric field clamped only to `Math.max(1, …)`
 * on both the dialog and the write path, so 5 is not exotic.
 *
 * The resulting state is the same one a failed fetch produces — no caps, so no
 * pre-warning — and reserve/charge stay authoritative, so nothing oversells.
 * Raising the ceiling means changing the wire format: 192 ISO instants is
 * already ~4.8KB of query string, and 288 would sit against nginx's default
 * 8k header buffer. Sending offsets from the anchor instead of whole instants
 * would cost ~5 bytes each and lift the ceiling by an order of magnitude, if a
 * partner ever configures a grid this fine.
 */
export const MAX_RENTAL_POOL_STARTS = 192;

/**
 * Equipment caps for the rental picker, joined to the client's start options.
 *
 * THE ALIGNMENT PROBLEM this module exists to solve: the client's start
 * options are a UNION of arithmetic sequences, one per opening window, each
 * stepping from its own `startMin` (startOptions.ts:262-277). They are not one
 * sequence, so no single anchor + step can name them all.
 *
 * This module therefore sends the server the exact instants it wants priced
 * (`starts=` on the pool-availability route) and joins the answer back by
 * instant. Every rendered start gets a cap, whatever shape the day is.
 *
 * ⚠ It used to send ONE anchor instead and let the server step by the ticket's
 * fraction size, which silently lost any start that was not a whole number of
 * fractions from that anchor. On the live "Electric Mountain Bike Rental"
 * listing — windows 09:30-13:00, 09:30-17:00, 13:30-17:00 and 14:00-17:00 on a
 * 90-minute fraction — the anchored grid from 09:30 named 09:30/11:00/12:30/
 * 14:00/15:30, so the 13:30 and 15:00 starts came back with no cap and stayed
 * clickable at any quantity. Anchoring cannot be repaired by moving the anchor:
 * on a day with two windows there is no instant both sequences sit on.
 *
 * FAIL OPEN throughout: a failed fetch, a poolless ticket, or a start the
 * server did not return is ABSENT from the map, and absent means uncapped.
 * Never zero. Reserve and charge stay authoritative.
 *
 * KNOWN LIMITATION, fail-open and deliberate: a day offering more than
 * {@link MAX_RENTAL_POOL_STARTS} starts has the excess truncated, which leaves
 * the day's quantity bound unknown — see that constant. Sending explicit
 * instants is what retired the three
 * limitations this module used to carry — off-grid second windows, the two DST
 * transition days (the client builds each instant with `wallClockInstant` and
 * the server echoes it back, so the two cannot drift), and a day spanning more
 * than 24h from its first start.
 */

/**
 * Every start option as a real instant, in the order the picker renders them.
 *
 * These are both what the request asks the server to price AND the keys the
 * response is joined back on, so the two cannot disagree — the same
 * `wallClockInstant` call produces both.
 */
export function rentalPoolStartIsos(params: {
  startOptions: number[];
  date: Date;
  timezone: string;
}): string[] {
  const { startOptions, date, timezone } = params;
  if (!startOptions.length) return [];
  const dayStr = rentalDayString(date);
  return startOptions
    .slice(0, MAX_RENTAL_POOL_STARTS)
    .map((minutesOfDay) =>
      wallClockInstant({ date: dayStr, timezone, minutesOfDay }).toISOString(),
    );
}

/** startMinutes → maxTickets. An ABSENT key is uncapped, never zero. */
export function rentalPoolCapsByStartMinutes(params: {
  response: PoolAvailabilityResponse | null;
  startOptions: number[];
  date: Date;
  timezone: string;
}): Map<number, number> {
  const { response, startOptions, date, timezone } = params;
  const caps = new Map<number, number>();
  if (!response || !Array.isArray(response.windows) || !response.windows.length) {
    return caps;
  }
  // Typed explicitly: `new Map(windows.map(w => [w.key, w]))` infers
  // Map<any, {}> — the callback returns an array, not a tuple — and the
  // lookup below then has no `maxTickets` to read. The tests could not catch
  // that, because vitest does not typecheck.
  const byKey = new Map<string, PoolAvailabilityWindow>(
    // `w` annotated because Array.isArray narrows an optional array property
    // to `any[]`, not to its declared element type.
    response.windows.map((w: PoolAvailabilityWindow): [string, PoolAvailabilityWindow] => [
      w.key,
      w,
    ]),
  );
  const dayStr = rentalDayString(date);
  for (const minutesOfDay of startOptions) {
    const iso = wallClockInstant({ date: dayStr, timezone, minutesOfDay }).toISOString();
    const hit = byKey.get(iso);
    if (!hit) continue; // no information for this start — stays uncapped
    if (typeof hit.maxTickets !== "number" || !Number.isFinite(hit.maxTickets)) {
      continue;
    }
    caps.set(minutesOfDay, Math.max(0, hit.maxTickets));
  }
  return caps;
}

/**
 * The most any single start on this day can take — the bound for the quantity
 * stepper, because the customer is free to pick whichever start fits.
 *
 * null = UNKNOWN = uncapped, and that includes the case where only SOME starts
 * carry a cap. The max of the known subset is not the day's cap: an uncapped
 * start is one checkout would still sell, so reporting the known maximum
 * under-offers — and reporting 0, when every known start happens to be
 * exhausted, would hide the entire grid behind "not enough equipment" while
 * bookable starts sat in it. Partial knowledge has to read as no knowledge.
 *
 * (This is the OPPOSITE of `poolCapsForKeys`, which caps on partial knowledge.
 * There the unknown windows are ones the customer has also selected, so the
 * tightest known window still binds the whole all-or-nothing purchase. Here
 * the customer picks exactly one start, so an unknown one is an alternative
 * that the known ones say nothing about.)
 */
export function rentalDayPoolCap(
  caps: Map<number, number>,
  startOptions: number[],
): number | null {
  if (!startOptions.length) return null;
  let best: number | null = null;
  for (const m of startOptions) {
    const cap = caps.get(m);
    if (cap == null) return null; // one unknown start makes the day unknown
    best = best == null ? cap : Math.max(best, cap);
  }
  return best;
}
